import { format, isValid, parseISO } from 'date-fns';

import { GRAY1 } from 'constants/colors';
import { WaterBalanceSeries } from './waterBalanceSeries';

// Subtítulo dos gráficos da Visão Detalhada do BH Sequencial e do BH por Cultura
// (#2009, itens 3 e 4), no formato do exemplo da cliente:
//   Local: Primavera do Leste - MT | Período: 25/09/2026 a 02/10/2026
//   | Data da rodada do período de previsão: 25/09/2026   (uma linha só, se couber)

const SEPARATOR = ' | ';

// Abaixo desta largura do gráfico a linha inteira (~110 caracteres) não cabe e a
// data da rodada desce para a segunda linha (regra `media` do ECharts).
export const SUBTITLE_WRAP_MAX_WIDTH = 760;

// Alturas em px a partir do topo do canvas: título (18px) + subtítulo (12px).
const SUBTITLE_LINE_HEIGHT = 16;
const LEGEND_TOP = 54;
const GRID_TOP_WITH_LEGEND = 96;
const GRID_TOP_WITHOUT_LEGEND = 78;

// undefined para data inválida: o date-fns lança RangeError no format e derrubava o card.
const formatDay = (isoDay: string, pattern = 'dd/MM/yyyy') => {
  const day = parseISO(isoDay);
  return isValid(day) ? format(day, pattern) : undefined;
};

// Chip "Rodada: DD/MM/AA" do card mobile; sem chip se não houver rodada ou ela for inválida.
export function formatRunDateChip(runDate: string | null | undefined): string | undefined {
  const day = runDate ? formatDay(runDate, 'dd/MM/yy') : undefined;
  return day && `Rodada: ${day}`;
}

function periodOf(series: Pick<WaterBalanceSeries, 'data'>[]): string | undefined {
  const days = (series[0]?.data ?? [])
    .map((point) => (point.date ? formatDay(point.date) : undefined))
    .filter((day): day is string => Boolean(day));
  if (days.length === 0) return undefined;
  const first = days[0];
  const last = days[days.length - 1];
  return first === last ? `Período: ${first}` : `Período: ${first} a ${last}`;
}

export function buildChartSubtitle({
  locationName,
  series,
  runDate,
}: {
  locationName?: string;
  series: Pick<WaterBalanceSeries, 'data'>[];
  runDate: string | null;
}): string[] {
  const period = periodOf(series);
  const runDay = runDate ? formatDay(runDate) : undefined;
  return [
    locationName && `Local: ${locationName}`,
    period,
    runDay && `Data da rodada do período de previsão: ${runDay}`,
  ].filter((part): part is string => Boolean(part));
}

interface TitledOption {
  title?: Record<string, unknown>;
  legend?: Record<string, unknown>;
  grid?: Record<string, unknown>;
  [key: string]: unknown;
}

function layout(option: TitledOption, subtext: string, extraLines: number) {
  const shift = extraLines * SUBTITLE_LINE_HEIGHT;
  return {
    title: { ...option.title, subtext },
    ...(option.legend && { legend: { ...option.legend, top: LEGEND_TOP + shift } }),
    grid: {
      ...option.grid,
      top: (option.legend ? GRID_TOP_WITH_LEGEND : GRID_TOP_WITHOUT_LEGEND) + shift,
    },
  };
}

export function applyChartSubtitle<T extends TitledOption>(
  option: T,
  parts: string[],
): T | Record<string, unknown> {
  if (parts.length === 0 || !option.title) return option;

  const base = {
    ...option,
    ...layout(option, parts.join(SEPARATOR), 0),
  };
  base.title = {
    ...base.title,
    itemGap: 6,
    subtextStyle: { color: GRAY1, fontSize: 12, lineHeight: SUBTITLE_LINE_HEIGHT },
  };

  // Só a linha com a data da rodada passa da largura; sem ela, cabe sempre e a
  // regra estreita repete a base. A regra vem SEMPRE: o ECharts só substitui as
  // `media` quando a opção nova traz `media` (OptionManager), então omiti-la faria
  // um gráfico estreito herdar o subtítulo da consulta anterior.
  const wraps = parts.length >= 3;
  const narrowSubtext = wraps
    ? `${parts.slice(0, -1).join(SEPARATOR)}\n${parts[parts.length - 1]}`
    : parts.join(SEPARATOR);
  return {
    baseOption: base,
    media: [{
      query: { maxWidth: SUBTITLE_WRAP_MAX_WIDTH },
      option: layout(base, narrowSubtext, wraps ? 1 : 0),
    }],
  };
}
